import { useEffect, useRef, useCallback, useState } from 'react'
import { useAuth } from '@/context/AuthContext'
import { getApiUrl } from '@/lib/apiUrl'

interface UseAutoLogoutOptions {
  timeout?: number // timeout in minutes, default 30
  warningTime?: number // warning time in minutes before logout, default 5
  onWarning?: () => void // callback when warning is shown
  onLogout?: () => void // callback when user is logged out
}

export function useAutoLogout({
  timeout = 30,
  warningTime = 5,
  onWarning,
  onLogout
}: UseAutoLogoutOptions = {}) {
  const { logout } = useAuth()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [showWarning, setShowWarning] = useState(false)
  const [timeLeft, setTimeLeft] = useState(0)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)

  // Convert minutes to milliseconds
  const timeoutMs = timeout * 60 * 1000
  const warningTimeMs = warningTime * 60 * 1000

  // Handle logout
  const handleLogout = useCallback(async () => {
    try {
      console.log('Auto-logout triggered due to inactivity')
      
      // Clear all timeouts
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
      if (countdownRef.current) clearTimeout(countdownRef.current)
      
      setShowWarning(false)
      onLogout?.()
      
      // Use centralized logout from AuthContext
      logout()
    } catch (error) {
      console.error('Auto-logout error:', error)
    }
  }, [onLogout, logout])

  // Handle warning
  const handleWarning = useCallback(() => {
    setShowWarning(true)
    setTimeLeft(warningTime * 60) // Set countdown in seconds
    onWarning?.()

    // Start countdown
    countdownRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) clearInterval(countdownRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [warningTime, onWarning])

  // Reset timeout
  const resetTimeout = useCallback(() => {
    // Clear existing timeouts
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
    if (countdownRef.current) clearInterval(countdownRef.current)
    
    setShowWarning(false)
    setTimeLeft(0)

    // Set warning timeout
    warningTimeoutRef.current = setTimeout(() => {
      handleWarning()
    }, timeoutMs - warningTimeMs)

    // Set logout timeout
    timeoutRef.current = setTimeout(() => {
      handleLogout()
    }, timeoutMs)
  }, [timeoutMs, warningTimeMs, handleWarning, handleLogout])

  // Extend session (dismiss warning and reset timer)
  const extendSession = useCallback(() => {
    resetTimeout()
  }, [resetTimeout])

  // Track user activity
  useEffect(() => {
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ]

    // Reset timeout on any user activity
    const resetOnActivity = () => {
      resetTimeout()
    }

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetOnActivity, true)
    })

    // Initial timeout setup
    resetTimeout()

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetOnActivity, true)
      })
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [resetTimeout])

  // Check if user is still authenticated by verifying with backend
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken')
        if (!token) {
          // No token means not authenticated, trigger logout
          handleLogout()
          return
        }

        const apiUrl = getApiUrl()
        
        const response = await fetch(`${apiUrl}/auth/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) {
          // Token is invalid or expired, trigger logout
          console.log('Auth check failed, logging out...')
          handleLogout()
        }
      } catch (error) {
        console.error('Auth check error:', error)
        // Don't logout on network errors - could be temporary
      }
    }

    // Check auth immediately and then periodically
    checkAuth()
    
    // Periodic auth check every 5 minutes
    const authCheckInterval = setInterval(checkAuth, 5 * 60 * 1000)
    
    return () => clearInterval(authCheckInterval)
  }, [handleLogout])

  return {
    showWarning,
    timeLeft,
    extendSession,
    formatTimeLeft: (seconds: number) => {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
  }
}
